import type { Invoice } from "api";
import cn from "classnames";
import { formatTimestamp } from "utils/date.utils";
import { formatInvoiceMoney } from "./credits.model";

type InvoicesTableProps = {
  invoices: Invoice[];
  portalBusy: boolean;
  /** Left out when the portal cannot be opened, which hides the link. */
  onOpenPortal?: () => void;
};

const STATUS: Record<string, { label: string; tone: string }> = {
  paid: { label: "Paid", tone: "ok" },
  open: { label: "Due", tone: "warn" },
  draft: { label: "Draft", tone: "" },
  void: { label: "Void", tone: "" },
  uncollectible: { label: "Unpaid", tone: "bad" },
};

/** Stripe's invoices for the company. Payments and cards live in the portal. */
export const InvoicesTable = ({ invoices, portalBusy, onOpenPortal }: InvoicesTableProps) => (
  <div className="pcx">
    <div className="pcx__top">
      <div>
        <div className="pcx__h">Invoices</div>
        <p className="pcx__sub">Issued by Stripe · full history in the billing portal</p>
      </div>
      {onOpenPortal !== undefined && (
        <button type="button" className="pcx__link" disabled={portalBusy} onClick={onOpenPortal}>
          Open billing portal
        </button>
      )}
    </div>

    <div className="pc-scroll">
      <table className="pc-tbl">
        <thead>
          <tr>
            <th>Date</th>
            <th>Invoice</th>
            <th>Amount</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice, index) => {
            const status = STATUS[invoice.status] ?? { label: invoice.status, tone: "" };
            const link = invoice.invoice_pdf ?? invoice.hosted_invoice_url;

            return (
              <tr key={invoice.number ?? `${invoice.date}-${index}`}>
                <td>{formatTimestamp(invoice.date)}</td>
                <td>{invoice.number ?? "-"}</td>
                <td>{formatInvoiceMoney(invoice.total, invoice.currency)}</td>
                <td>
                  <span className={cn("pc-tag", { [`pc-tag--${status.tone}`]: status.tone !== "" })}>
                    {status.label}
                  </span>
                </td>
                <td>
                  {link !== null && (
                    <a className="pcx__link" href={link} target="_blank" rel="noopener noreferrer">
                      {invoice.invoice_pdf !== null ? "PDF" : "View"}
                    </a>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>

    <p className="pc-foot">Payments, card details and receipts are managed in the Stripe billing portal.</p>
  </div>
);
